import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Store,
  MapPin,
  Phone,
  Mail,
  Star,
  Package,
  CheckCircle,
  ShoppingCart,
  ArrowLeft,
  Building2,
  Calendar,
  TrendingUp,
  Home,
  MessageSquare,
  Send
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SellerPublicPage() {
  const { sellerCode } = useParams();
  const [seller, setSeller] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    fetchSellerData();
  }, [sellerCode]);

  const fetchSellerData = async () => {
    try {
      const res = await axios.get(`${API}/api/sellers/public/${sellerCode}`);
      setSeller(res.data);
      setProducts(res.data.products || []);
      
      // Fetch reviews
      const reviewsRes = await axios.get(`${API}/api/sellers/${res.data.seller_id}/reviews`);
      setReviews(reviewsRes.data || []);
    } catch (err) {
      setError("Selger ikke funnet");
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Du må være logget inn for å skrive en anmeldelse");
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/api/sellers/${seller.seller_id}/review`, null, {
        params: {
          rating: reviewRating,
          comment: reviewComment
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Takk for din anmeldelse!");
      setShowReviewForm(false);
      setReviewComment("");
      fetchSellerData(); // Refresh to show new review
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende anmeldelse");
    } finally {
      setSubmittingReview(false);
    }
  };

  const addToCart = (product) => {
    const cart = JSON.parse(localStorage.getItem("webshop_cart") || "[]");
    const existing = cart.find(item => item.product_id === product.product_id);
    
    if (existing) {
      existing.quantity += 1;
    } else {
      cart.push({
        product_id: product.product_id,
        name: product.name,
        price: product.price,
        photo: product.photos?.[0],
        quantity: 1,
        seller_id: seller.seller_id,
        seller_code: seller.seller_code,
        seller_name: seller.company_name
      });
    }
    
    localStorage.setItem("webshop_cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cart-updated"));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]">
        {/* Simple Header */}
        <header className="bg-[#0A0A0A] border-b border-white/10 py-4">
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white font-bold">
              <Home className="w-5 h-5" />
              AllStillas
            </Link>
            <Link to="/nettbutikk">
              <Button variant="outline" className="border-white/20 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tilbake til Butikk
              </Button>
            </Link>
          </div>
        </header>
        
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !seller) {
    return (
      <div className="min-h-screen bg-[#050505]">
        {/* Simple Header */}
        <header className="bg-[#0A0A0A] border-b border-white/10 py-4">
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-white font-bold">
              <Home className="w-5 h-5" />
              AllStillas
            </Link>
            <Link to="/nettbutikk">
              <Button variant="outline" className="border-white/20 text-white">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tilbake til Butikk
              </Button>
            </Link>
          </div>
        </header>
        
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <Store className="w-16 h-16 mx-auto mb-4 text-[#94A3B8]" />
          <h1 className="text-2xl font-bold text-white mb-4">Selger ikke funnet</h1>
          <p className="text-[#94A3B8] mb-6">
            Vi kunne ikke finne selgeren du leter etter.
          </p>
          <Link to="/nettbutikk">
            <Button className="bg-[#F7931A] text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake til Nettbutikk
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Simple Header */}
      <header className="bg-[#0A0A0A] border-b border-white/10 py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white font-bold">
            <Home className="w-5 h-5" />
            AllStillas
          </Link>
          <Link to="/nettbutikk">
            <Button variant="outline" className="border-white/20 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tilbake til Butikk
            </Button>
          </Link>
        </div>
      </header>
      
      {/* Hero Banner */}
      <div className="relative">
        {seller.banner_url ? (
          <div 
            className="h-48 md:h-64 bg-cover bg-center"
            style={{ backgroundImage: `url(${seller.banner_url})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
          </div>
        ) : (
          <div className="h-48 md:h-64 bg-gradient-to-r from-[#F7931A]/20 via-[#23F7DD]/10 to-[#F7931A]/20">
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
          </div>
        )}
        
        {/* Seller Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex items-end gap-6">
              {/* Logo */}
              {seller.logo_url ? (
                <img 
                  src={seller.logo_url} 
                  alt={seller.company_name}
                  className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-4 border-[#050505] object-cover shadow-xl"
                />
              ) : (
                <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-4 border-[#050505] bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center shadow-xl">
                  <Store className="w-12 h-12 text-white" />
                </div>
              )}
              
              {/* Name & Badges */}
              <div className="pb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl md:text-3xl font-['Unbounded'] font-bold text-white">
                    {seller.company_name}
                  </h1>
                  {seller.verified && (
                    <Badge className="bg-blue-500/20 text-blue-400 border border-blue-500/30">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verifisert
                    </Badge>
                  )}
                </div>
                <p className="text-[#94A3B8] font-mono text-sm">{seller.seller_code}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        {/* Stats Bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#F7931A]/20 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 text-[#F7931A]" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{products.length}</p>
                <p className="text-[#94A3B8] text-xs">Produkter</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-yellow-500/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white flex items-center">
                  {seller.rating || 0}
                  <span className="text-sm text-[#94A3B8] ml-1">/ 5</span>
                </p>
                <p className="text-[#94A3B8] text-xs">{seller.review_count || 0} anmeldelser</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-[#23F7DD]/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[#23F7DD]" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">{seller.total_sales || 0}</p>
                <p className="text-[#94A3B8] text-xs">Solgte enheter</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-xl font-bold text-white">
                  {new Date(seller.created_at).toLocaleDateString('no-NO', { month: 'short', year: 'numeric' })}
                </p>
                <p className="text-[#94A3B8] text-xs">Medlem siden</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* About & Contact */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Description */}
          <Card className="bg-[#0A0A0A] border-white/10 md:col-span-2">
            <CardContent className="p-6">
              <h2 className="text-white font-semibold mb-3">Om {seller.company_name}</h2>
              <p className="text-[#94A3B8]">
                {seller.description || "Ingen beskrivelse tilgjengelig."}
              </p>
            </CardContent>
          </Card>
          
          {/* Contact Info */}
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 space-y-4">
              <h2 className="text-white font-semibold">Kontakt</h2>
              
              {seller.contact_email && (
                <a href={`mailto:${seller.contact_email}`} className="flex items-center gap-3 text-[#94A3B8] hover:text-[#23F7DD] transition-colors">
                  <Mail className="w-4 h-4" />
                  <span className="text-sm">{seller.contact_email}</span>
                </a>
              )}
              
              {seller.contact_phone && (
                <a href={`tel:${seller.contact_phone}`} className="flex items-center gap-3 text-[#94A3B8] hover:text-[#23F7DD] transition-colors">
                  <Phone className="w-4 h-4" />
                  <span className="text-sm">{seller.contact_phone}</span>
                </a>
              )}
              
              {seller.address?.city && (
                <div className="flex items-center gap-3 text-[#94A3B8]">
                  <MapPin className="w-4 h-4" />
                  <span className="text-sm">
                    {seller.address.city}, {seller.address.country || 'Norge'}
                  </span>
                </div>
              )}
              
              {seller.org_number && (
                <div className="flex items-center gap-3 text-[#94A3B8]">
                  <Building2 className="w-4 h-4" />
                  <span className="text-sm">Org.nr: {seller.org_number}</span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Products */}
        <div data-testid="seller-products-section">
          <h2 className="text-2xl font-['Unbounded'] font-bold text-white mb-6">
            Produkter fra {seller.company_name}
          </h2>
          
          {products.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10">
              <CardContent className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-[#94A3B8] opacity-50" />
                <p className="text-[#94A3B8]">Ingen produkter tilgjengelig ennå</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map(product => (
                <Card 
                  key={product.product_id} 
                  className="bg-[#0A0A0A] border-white/10 overflow-hidden group hover:border-[#F7931A]/50 transition-colors"
                >
                  {/* Product Image */}
                  <Link to={`/webshop/product/${product.product_id}`}>
                    <div className="aspect-square relative bg-white/5 overflow-hidden">
                      {product.photos && product.photos.length > 0 ? (
                        <img 
                          src={product.photos[0]} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-12 h-12 text-[#94A3B8]/30" />
                        </div>
                      )}
                      
                      {/* Stock Badge */}
                      {product.stock_quantity <= 0 && (
                        <Badge className="absolute top-2 right-2 bg-red-500/80 text-white">
                          Utsolgt
                        </Badge>
                      )}
                    </div>
                  </Link>

                  <CardContent className="p-4">
                    <Link to={`/webshop/product/${product.product_id}`}>
                      <p className="text-[#F7931A] text-xs uppercase tracking-wider mb-1">
                        {product.category}
                      </p>
                      <h3 className="text-white font-semibold mb-2 line-clamp-2 hover:text-[#23F7DD] transition-colors">
                        {product.name}
                      </h3>
                    </Link>
                    
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xl font-bold text-white">
                        {product.price?.toLocaleString()} <span className="text-sm text-[#94A3B8]">NOK</span>
                      </p>
                      <Button
                        size="sm"
                        onClick={() => addToCart(product)}
                        disabled={product.stock_quantity <= 0}
                        className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Reviews Section */}
        <div className="mt-16" data-testid="seller-reviews-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
              <MessageSquare className="w-6 h-6 text-[#F7931A]" />
              Anmeldelser ({reviews.length})
            </h2>
            <Button
              onClick={() => setShowReviewForm(!showReviewForm)}
              className="bg-[#23F7DD] text-black hover:bg-[#23F7DD]/80 font-semibold"
              data-testid="toggle-review-form-btn"
            >
              <Star className="w-4 h-4 mr-2" />
              {showReviewForm ? "Avbryt" : "Skriv anmeldelse"}
            </Button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <Card className="bg-[#0A0A0A] border-[#23F7DD]/30 mb-6" data-testid="review-form-card">
              <CardContent className="p-6 space-y-4">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    Din vurdering
                  </label>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setReviewRating(n)}
                        className="transition-transform hover:scale-110"
                        data-testid={`review-star-${n}`}
                      >
                        <Star
                          className={`w-8 h-8 ${
                            n <= reviewRating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-[#94A3B8]"
                          }`}
                        />
                      </button>
                    ))}
                    <span className="text-[#94A3B8] ml-3 text-sm">
                      {reviewRating} / 5
                    </span>
                  </div>
                </div>

                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    Din kommentar (valgfritt)
                  </label>
                  <Textarea
                    value={reviewComment}
                    onChange={(e) => setReviewComment(e.target.value)}
                    placeholder="Del din erfaring med denne selgeren..."
                    className="bg-black/40 border-white/20 text-white min-h-[100px]"
                    data-testid="review-comment-input"
                  />
                </div>

                <div className="flex gap-2 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowReviewForm(false);
                      setReviewComment("");
                      setReviewRating(5);
                    }}
                    className="border-white/20 text-white"
                    data-testid="cancel-review-btn"
                  >
                    Avbryt
                  </Button>
                  <Button
                    onClick={submitReview}
                    disabled={submittingReview}
                    className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80"
                    data-testid="submit-review-btn"
                  >
                    {submittingReview ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                        Sender...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send anmeldelse
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <Card className="bg-[#0A0A0A] border-white/10" data-testid="no-reviews-card">
              <CardContent className="p-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-[#94A3B8] opacity-50" />
                <p className="text-[#94A3B8] mb-2">Ingen anmeldelser ennå</p>
                <p className="text-[#94A3B8] text-sm">
                  Vær den første til å anmelde {seller.company_name}!
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="reviews-list">
              {reviews.map((review, idx) => (
                <Card
                  key={review.review_id || idx}
                  className="bg-[#0A0A0A] border-white/10"
                  data-testid={`review-item-${idx}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#F7931A] to-[#FFCD00] flex items-center justify-center text-white font-bold">
                          {(review.user_name || "A")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-semibold">
                            {review.user_name || "Anonym"}
                          </p>
                          <p className="text-[#94A3B8] text-xs">
                            {review.created_at
                              ? new Date(review.created_at).toLocaleDateString("no-NO", {
                                  day: "numeric",
                                  month: "long",
                                  year: "numeric"
                                })
                              : ""}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star
                            key={n}
                            className={`w-4 h-4 ${
                              n <= (review.rating || 0)
                                ? "fill-yellow-400 text-yellow-400"
                                : "text-[#94A3B8]/30"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-[#94A3B8] leading-relaxed">{review.comment}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Simple Footer */}
      <footer className="bg-[#0A0A0A] border-t border-white/10 py-8 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-[#94A3B8] text-sm">
            © {new Date().getFullYear()} AllStillas Marketplace. Alle rettigheter reservert.
          </p>
        </div>
      </footer>
    </div>
  );
}
